import { OTRSelect } from '../components/OTRSelect';
import { OTRDateField } from '../components/OTRDateField';
import React, { useState, useEffect } from 'react';
import { Trash2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { AppLayout, PageBackLink } from '../../../components/layout';
import { CityTierTabs } from '../components';
import { CITY_TIERS, APPROVAL_FLOW, BUDGET_TYPE } from '../constants/otrEnums';
import { getTravelPolicySettings, saveTravelPolicySettings, getExpensePoliciesByTier, saveExpensePolicy } from '../../../services/outstationTravelService';

export const TravelPolicyConfigPage = () => {
  const [activeTier, setActiveTier] = useState(CITY_TIERS.TIER_1);
  const [settings, setSettings] = useState({ approvalFlow: APPROVAL_FLOW.MANAGER_THEN_FINANCE, exists: false });
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [dirtyIds, setDirtyIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  // Values match the backend ModeName enum exactly.
  const MODE_OPTIONS = ['NA', 'Bus', 'Train', 'Cab', 'Auto', 'Flight'];

  const [showAddModal, setShowAddModal] = useState(false);
  const emptyDraft = {
    headName: '',
    modeName: 'NA',
    budgetType: BUDGET_TYPE.PER_DAY,
    defaultAmount: 0,
    companyArranged: false,
  };
  const [newHeadDraft, setNewHeadDraft] = useState(emptyDraft);

  const loadTier = async (tier) => {
    const [s, heads] = await Promise.all([
      getTravelPolicySettings(tier),
      getExpensePoliciesByTier(null, tier),
    ]);
    setSettings(
      s
        ? { ...s, exists: true }
        : { approvalFlow: APPROVAL_FLOW.MANAGER_THEN_FINANCE, isActive: true, exists: false }
    );
    setExpenseHeads(heads);
    setDirtyIds([]);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadTier(activeTier);
      } catch (err) {
        if (!cancelled) console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTier]);

  const handleHeadChange = (id, field, value) => {
    setExpenseHeads(prev => prev.map(h => h.headId === id ? { ...h, [field]: value } : h));
    setDirtyIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  };

  const handleOpenAddModal = () => {
    setNewHeadDraft(emptyDraft);
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => setShowAddModal(false);

  // Creates the head on the backend under the active tier, then re-reads the
  // tier's heads so the table always mirrors what is stored.
  const handleConfirmAddExpenseHead = async () => {
    if (!newHeadDraft.headName.trim() || adding) return;
    setAdding(true);
    try {
      await saveExpensePolicy({
        ...newHeadDraft,
        headName: newHeadDraft.headName.trim(),
        tierId: activeTier,
        isActive: true,
      });
      const heads = await getExpensePoliciesByTier(null, activeTier);
      setExpenseHeads(heads);
      setShowAddModal(false);
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Could not add expense head');
    } finally {
      setAdding(false);
    }
  };

  // The API has no delete; removing a head deactivates it for the tier.
  const handleRemoveExpenseHead = async (id) => {
    if (!window.confirm('Remove this expense head from the tier?')) return;
    const head = expenseHeads.find(h => h.headId === id);
    if (!head) return;
    try {
      await saveExpensePolicy({ ...head, isActive: false });
      setExpenseHeads(await getExpensePoliciesByTier(null, activeTier));
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Could not remove expense head');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveTravelPolicySettings({ ...settings, tierId: activeTier });
      await Promise.all(
        expenseHeads
          .filter(h => dirtyIds.includes(h.headId))
          .map(h => saveExpensePolicy(h))
      );
      await loadTier(activeTier);
      alert('Policy saved successfully');
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Could not save policy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AppLayout><div className="py-20 text-center">Loading Configuration...</div></AppLayout>;

  return (
    <AppLayout>
      <PageBackLink to="/" />

      <div className="mb-8">
        <h1 className="text-[28px] font-bold text-foreground leading-tight">Travel Policy Configuration</h1>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">Define approval workflows and expense limits per city tier.</p>
      </div>

      <CityTierTabs activeTier={activeTier} onTierChange={setActiveTier} />

      <div className="bg-white rounded-2xl sm:rounded-[2rem] border border-[var(--color-border)] shadow-sm overflow-hidden">
        <div className="flex items-start justify-between gap-6 flex-wrap p-4 sm:p-6 border-b border-[var(--color-border)]">
          <div>
            <h3 className="text-lg font-bold text-foreground">Expense heads</h3>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">All amounts display in the tenant base currency ({settings.currencySymbol || '₹'}) read from the Currencies master.</p>
          </div>
          <div className="flex items-end gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Approval Flow</label>
              <OTRSelect
                value={settings.approvalFlow}
                onChange={(v) => setSettings({ ...settings, approvalFlow: v })}
                options={[{ value: APPROVAL_FLOW.MANAGER, label: 'Manager' }, { value: APPROVAL_FLOW.MANAGER_THEN_FINANCE, label: 'Manager then Finance' }, { value: APPROVAL_FLOW.FINANCE, label: 'Finance' }, { value: APPROVAL_FLOW.FINANCE_THEN_MANAGER, label: 'Finance then Manager' }]}
                className="min-w-[220px]"
              />
            </div>
            <Button
              onClick={handleOpenAddModal}
              className="h-11 px-6 rounded-xl bg-[var(--color-primary)] text-white font-bold shadow-lg hover:bg-[var(--color-accent)]"
            >
              Add Custom Head
            </Button>
          </div>
        </div>

        <div><table className="max-md:block w-full text-left border-collapse">
          <thead className="max-md:hidden">
            <tr className="bg-[var(--color-workspace)]/30 border-b border-[var(--color-border)]">
              <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">#</th>
              <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Expense Head</th>
              <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Mode</th>
              <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Budget Type</th>
              <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Default Amount</th>
              <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-center">Company Arranged</th>
              <th className="px-6 py-4 w-10"></th>
            </tr>
          </thead>
          <tbody className="max-md:block divide-y divide-[var(--color-border)]">
            {expenseHeads.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-sm text-[var(--color-text-secondary)]">
                  No expense heads configured for this tier yet. Click "Add Custom Head" to create one.
                </td>
              </tr>
            )}
            {expenseHeads.map((head, idx) => (
              <tr key={head.headId} className="max-md:block max-md:px-4 max-md:py-3 hover:bg-[var(--color-workspace)]/10 transition-colors">
                <td data-label="#" className="max-md:flex max-md:flex-col max-md:items-stretch max-md:gap-1 max-md:px-0 max-md:py-1.5 max-md:text-left max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-[var(--color-text-secondary)] px-6 py-4 text-sm text-[var(--color-text-secondary)]">{idx + 1}</td>
                <td data-label="Expense Head" className="max-md:flex max-md:flex-col max-md:items-stretch max-md:gap-1 max-md:px-0 max-md:py-1.5 max-md:text-left max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-[var(--color-text-secondary)] px-6 py-4">
                  <input
                    type="text"
                    placeholder="e.g. Lodging"
                    value={head.headName}
                    onChange={(e) => handleHeadChange(head.headId, 'headName', e.target.value)}
                    className="w-full h-9 px-3 text-sm font-bold bg-transparent border border-transparent hover:border-[var(--color-border)] rounded-lg outline-none focus:border-[var(--color-primary)] transition-all"
                  />
                </td>
                <td data-label="Mode" className="max-md:flex max-md:flex-col max-md:items-stretch max-md:gap-1 max-md:px-0 max-md:py-1.5 max-md:text-left max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-[var(--color-text-secondary)] px-6 py-4">
                  <OTRSelect
                    value={head.modeName || 'NA'}
                    onChange={(v) => handleHeadChange(head.headId, 'modeName', v)}
                    options={MODE_OPTIONS.map(m => ({ value: m, label: m }))}
                    compact
                  />
                </td>
                <td data-label="Budget Type" className="max-md:flex max-md:flex-col max-md:items-stretch max-md:gap-1 max-md:px-0 max-md:py-1.5 max-md:text-left max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-[var(--color-text-secondary)] px-6 py-4">
                  <OTRSelect
                    value={head.budgetType}
                    onChange={(v) => handleHeadChange(head.headId, 'budgetType', v)}
                    options={Object.values(BUDGET_TYPE).map(t => ({ value: t, label: t }))}
                    compact
                  />
                </td>
                <td data-label="Default Amount" className="max-md:flex max-md:flex-col max-md:items-stretch max-md:gap-1 max-md:px-0 max-md:py-1.5 max-md:text-left max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-[var(--color-text-secondary)] px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[var(--color-text-secondary)]">{settings.currencySymbol || '₹'}</span>
                    <input
                      type="number"
                      value={head.defaultAmount}
                      onChange={(e) => handleHeadChange(head.headId, 'defaultAmount', parseFloat(e.target.value) || 0)}
                      className="w-24 h-9 px-2 text-sm font-bold bg-white border border-[var(--color-text-border)] rounded-lg outline-none"
                    />
                  </div>
                </td>
                <td data-label="Company Arranged" className="max-md:flex max-md:flex-col max-md:items-stretch max-md:gap-1 max-md:px-0 max-md:py-1.5 max-md:text-left max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-[var(--color-text-secondary)] px-6 py-4 text-center">
                  <input
                    type="checkbox"
                    checked={head.companyArranged}
                    onChange={(e) => handleHeadChange(head.headId, 'companyArranged', e.target.checked)}
                    className="w-5 h-5 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]/20"
                  />
                </td>
                <td className="max-md:flex max-md:flex-col max-md:items-stretch max-md:gap-1 max-md:px-0 max-md:py-1.5 max-md:text-left max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-[var(--color-text-secondary)] px-6 py-4 text-center">
                  <button
                    type="button"
                    onClick={() => handleRemoveExpenseHead(head.headId)}
                    className="text-[var(--color-text-secondary)] hover:text-red-500 transition-colors"
                    aria-label="Remove expense head"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      <div className="flex justify-end mt-8">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="h-11 px-8 rounded-xl bg-[var(--color-primary)] text-white font-bold shadow-lg hover:bg-[var(--color-accent)]"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-foreground">Add Custom Expense Head</h3>
              <button
                type="button"
                onClick={handleCloseAddModal}
                className="text-[var(--color-text-secondary)] hover:text-foreground"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Expense Head Name <span className="text-[var(--color-error)]"> *</span></label>
                <input
                  type="text"
                  autoFocus
                  placeholder="e.g. Internet Reimbursement"
                  value={newHeadDraft.headName}
                  onChange={(e) => setNewHeadDraft({ ...newHeadDraft, headName: e.target.value })}
                  className="h-11 px-4 text-sm font-bold rounded-xl border border-[var(--color-text-border)] bg-white outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/10"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Mode</label>
                <OTRSelect
                  value={newHeadDraft.modeName || 'NA'}
                  onChange={(v) => setNewHeadDraft({ ...newHeadDraft, modeName: v })}
                  options={MODE_OPTIONS.map(m => ({ value: m, label: m }))}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Budget Type</label>
                <OTRSelect
                  value={newHeadDraft.budgetType}
                  onChange={(v) => setNewHeadDraft({ ...newHeadDraft, budgetType: v })}
                  options={Object.values(BUDGET_TYPE).map(t => ({ value: t, label: t }))}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Default Amount</label>
                <div className="flex items-center gap-2 h-11 px-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-workspace)]/20 focus-within:ring-2 focus-within:ring-[var(--color-primary)]/20">
                  <span className="text-sm font-bold text-[var(--color-text-secondary)]">{settings.currencySymbol || '₹'}</span>
                  <input
                    type="number"
                    value={newHeadDraft.defaultAmount}
                    onChange={(e) => setNewHeadDraft({ ...newHeadDraft, defaultAmount: parseFloat(e.target.value) || 0 })}
                    className="flex-1 bg-transparent text-sm font-bold outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between px-1">
                <label className="text-sm font-bold text-foreground">Company Arranged</label>
                <input
                  type="checkbox"
                  checked={newHeadDraft.companyArranged}
                  onChange={(e) => setNewHeadDraft({ ...newHeadDraft, companyArranged: e.target.checked })}
                  className="w-5 h-5 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]/20"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <Button
                onClick={handleCloseAddModal}
                className="h-11 px-6 rounded-xl bg-[var(--color-workspace)]/40 text-foreground font-bold hover:bg-[var(--color-workspace)]/70"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmAddExpenseHead}
                disabled={!newHeadDraft.headName.trim() || adding}
                className="h-11 px-6 rounded-xl bg-[var(--color-primary)] text-white font-bold shadow-lg hover:bg-[var(--color-accent)] disabled:opacity-50"
              >
                {adding ? 'Adding...' : 'Add Head'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};